import {
  AddEventToSeatedOpenAccessProtocol, RemoveEventFromSeatedOpenAccessProtocol,
} from '../models/serializable/open-access-pass/seated-open-access-protocol-modifier.js';
import { SeatedOpenAccessProtocol } from '../models/serializable/open-access-pass/seated-open-access-protocol.js';
import { SeatingOptions } from '../models/serializable/seating-options.js';
import { IllegalOperationException } from '../exceptions/illegal-operation-exception.js';
import { isNullOrUndefined } from './null-safety.js';
import { OpenAccessPassSeatHelper } from './open-access-pass-seat-helper.js';

export class SeatedOpenAccessProtocolHelper implements OpenAccessPassSeatHelper {
  private constructor(
      private protocol: SeatedOpenAccessProtocol | null,
      private passVariationId: string | null,
  ) {}

  static forPass(args: {
    protocol: SeatedOpenAccessProtocol,
    passVariationId: string,
  }): SeatedOpenAccessProtocolHelper {
    return new SeatedOpenAccessProtocolHelper(args.protocol, args.passVariationId);
  }

  static forTemplate(args: {
    protocol: SeatedOpenAccessProtocol,
  }): SeatedOpenAccessProtocolHelper {
    return new SeatedOpenAccessProtocolHelper(args.protocol, null);
  }

  static empty(): SeatedOpenAccessProtocolHelper {
    return new SeatedOpenAccessProtocolHelper(null, null);
  }

  static findModifiedSeats(
      protocol: SeatedOpenAccessProtocol,
  ): [eventId: string, seatId: number][] {
    const seats: [eventId: string, seatId: number][] = [];

    for(const modifier of protocol.modifiers.values()) {
      if(modifier instanceof AddEventToSeatedOpenAccessProtocol) {
        seats.push([modifier.eventId, modifier.seatingOptions.seatId]);
      }
    }

    return seats;
  }

  findModifiedSeats(): [eventId: string, seatId: number][] {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }
    return SeatedOpenAccessProtocolHelper.findModifiedSeats(this.protocol);
  }

  setSourcePass({ protocol, passVariationId }: { protocol: SeatedOpenAccessProtocol, passVariationId: string }): void {
    this.protocol = protocol;
    this.passVariationId = passVariationId;
  }

  setSourceTemplate({ protocol }: { protocol: SeatedOpenAccessProtocol }): void {
    this.protocol = protocol;
    this.passVariationId = null;
  }

  getSeatForEvent(eventId: string): SeatingOptions | null {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    let result: SeatingOptions | null = null;

    const globalSeatingOptions = this.protocol.seatingOptions;
    if(globalSeatingOptions != null && this.isEventAssigned(eventId)) {
      result = globalSeatingOptions;
    }

    for(const [modifierEventId, modifier] of this.protocol.modifiers) {
      if(modifierEventId == eventId) {
        switch(true) {
          case modifier instanceof AddEventToSeatedOpenAccessProtocol: {
            result = modifier.seatingOptions;
          } break;
          case modifier instanceof RemoveEventFromSeatedOpenAccessProtocol: {
            result = null;
          } break;
        }
      }
    }

    return result;
  }

  getSeatDistribution(assignedEventIds: Iterable<string>): Map<string, SeatingOptions> {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    const result = new Map<string, SeatingOptions>();

    const seatingOptions = this.protocol.seatingOptions;
    if(seatingOptions != null) {
      for(const eventId of assignedEventIds) {
        result.set(eventId, seatingOptions);
      }
    }

    for(const [eventId, modifier] of this.protocol.modifiers) {
      switch(true) {
        case modifier instanceof AddEventToSeatedOpenAccessProtocol: {
          result.set(eventId, modifier.seatingOptions);
        } break;
        case modifier instanceof RemoveEventFromSeatedOpenAccessProtocol: {
          result.delete(eventId);
        } break;
      }
    }

    return result;
  }

  addEvent(eventId: string, seatingOptions: SeatingOptions): void {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    this.protocol.modifiers.delete(eventId);

    const isCanonEvent = this.isCanonEvent(eventId);
    const isSameSeat = this.protocol.seatingOptions?.seatId === seatingOptions.seatId;

    if(isCanonEvent && isSameSeat) {
      return;
    }

    this.protocol.modifiers.set(
        eventId,
        AddEventToSeatedOpenAccessProtocol.create({
          eventId,
          seatingOptions,
        }),
    );
  }

  undoAddEvent(eventId: string): void {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    const modifier = this.protocol.modifiers.get(eventId);
    if(isNullOrUndefined(modifier)) {
      return;
    }

    if(modifier instanceof AddEventToSeatedOpenAccessProtocol) {
      this.protocol.modifiers.delete(eventId);
    }
  }

  removeEvent(eventId: string): void {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    const isCanonEvent = this.isCanonEvent(eventId);
    if(!isCanonEvent) {
      return;
    }

    this.protocol.modifiers.set(
        eventId,
        RemoveEventFromSeatedOpenAccessProtocol.create({
          eventId,
        }),
    );
  }

  undoRemoveEvent(eventId: string): void {
    if(this.protocol == null) {
      throw new IllegalOperationException('Cannot use [SeatedOpenAccessProtocolHelper] without a source attached');
    }

    const modifier = this.protocol.modifiers.get(eventId);
    if(isNullOrUndefined(modifier)) {
      return;
    }

    if(modifier instanceof RemoveEventFromSeatedOpenAccessProtocol) {
      this.protocol.modifiers.delete(eventId);
    }
  }

  isEventAssigned(eventId: string): boolean {
    return this.passVariationId != null ?
      this.protocol!.isEventAssignedToPass({ eventId, passVariationId: this.passVariationId }):
      this.protocol!.isEventAssignedToTemplate({ eventId });
  }

  private isCanonEvent(eventId: string): boolean {
    return this.passVariationId != null ?
      this.protocol!.isEventAssignedToPass({
        eventId,
        passVariationId: this.passVariationId,
      }):
      this.protocol!.isEventAssignedToTemplate({ eventId });
  }
}
